import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../lib/api";
import AppLayout from "../components/app/AppLayout";
import { CURRENCIES } from "../lib/currency";
import { ArrowLeft, ArrowRight, Sparkles, FileSearch, AlertTriangle, CheckCircle2, Cog, Upload, Clock } from "lucide-react";

export default function ImportTimeStudyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null); // { storage_path, analysis, filename }
  const [error, setError] = useState("");

  const [materials, setMaterials] = useState([]);
  const [machines, setMachines] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [materialId, setMaterialId] = useState("");
  const [ownedMachines, setOwnedMachines] = useState([]);
  const [batchSize, setBatchSize] = useState(1);
  const [marginPct, setMarginPct] = useState(25);
  const [currency, setCurrency] = useState("EUR");
  const [customerId, setCustomerId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const STEPS = [
    { id: 1, label: t("importTs.step1") },
    { id: 2, label: t("importTs.step2") },
    { id: 3, label: t("importTs.step3") },
    { id: 4, label: t("importTs.step4") },
  ];

  useEffect(() => {
    Promise.all([api.get("/catalog/materials"), api.get("/catalog/machines"), api.get("/customers")])
      .then(([m, mc, c]) => { setMaterials(m.data); setMachines(mc.data); setCustomers(c.data); })
      .catch(() => {});
  }, []);

  const handleFile = (f) => {
    if (!f) return;
    const lower = f.name.toLowerCase();
    const supported = [".pdf", ".xlsx", ".csv", ".docx", ".txt"];
    if (!supported.some((ext) => lower.endsWith(ext))) {
      setError(t("importTs.errorFormat"));
      return;
    }
    if (f.size > 10 * 1024 * 1024) { setError(t("importTs.errorSize")); return; }
    setError(""); setFile(f);
  };

  const parsePDF = async () => {
    if (!file) return;
    setParsing(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/quotes/parse-time-study", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParseResult(data);
      // Pre-suggest material from hint
      const hint = (data.analysis?.material_hint || "").toLowerCase();
      const suggested = materials.find((m) => hint.includes(m.name.split(" ")[0].toLowerCase()) || hint.includes(m.id.split("_")[0]));
      if (suggested) setMaterialId(suggested.id);
      setStep(2);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || t("importTs.errorParse"));
    }
    setParsing(false);
  };

  const toggleMachine = (id) => setOwnedMachines((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const canNext = () => {
    if (step === 1) return !!file;
    if (step === 2) return !!parseResult;
    if (step === 3) return !!materialId && ownedMachines.length > 0;
    return false;
  };

  const submit = async () => {
    setSaving(true); setError("");
    try {
      const { data } = await api.post("/quotes/import-time-study", {
        storage_path: parseResult.storage_path,
        material_id: materialId,
        owned_machine_ids: ownedMachines,
        batch_size: parseInt(batchSize) || 1,
        margin_pct: parseFloat(marginPct) || 25,
        currency,
        customer_id: customerId || null,
        analysis: parseResult.analysis,
        notes,
      });
      navigate(`/quotes/${data.id}`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || t("importTs.errorSave"));
    }
    setSaving(false);
  };

  const a = parseResult?.analysis;

  return (
    <AppLayout>
      <div className="px-8 py-8 max-w-6xl mx-auto" data-testid="import-time-study-page">
        <button onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/dashboard"))} className="text-slate-400 hover:text-slate-200 text-sm flex items-center gap-2 mb-4" data-testid="back-btn">
          <ArrowLeft size={14}/> {t("common.back")}
        </button>
        <div className="label-tech mb-2">{t("importTs.label")}</div>
        <h1 className="font-display font-bold text-3xl text-slate-100 mb-6">{t("importTs.title")}</h1>

        {/* Stepper */}
        <div className="flex items-center gap-3 mb-8">
          {STEPS.map((s, i) => {
            const active = step === s.id;
            const done = step > s.id;
            return (
              <div key={s.id} className="flex items-center gap-3 flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono-data border ${active ? "bg-blue-500 border-blue-500 text-white pulse-blue" : done ? "bg-blue-500/20 border-blue-500 text-blue-400" : "border-slate-700 text-slate-500"}`}>
                  {done ? <CheckCircle2 size={14}/> : s.id}
                </div>
                <div className={`text-sm hidden md:block ${active ? "text-slate-100" : done ? "text-blue-400" : "text-slate-500"}`}>{s.label}</div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px ${done ? "bg-blue-500/40" : "bg-slate-700"}`}/>}
              </div>
            );
          })}
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 text-sm flex items-center gap-2" data-testid="ts-error">
            <AlertTriangle size={16}/> {error}
          </div>
        )}

        <div className="tech-card p-8 fade-in-up">
          {step === 1 && (
            <div data-testid="step-upload-pdf">
              <div className="label-tech mb-2">{t("common.step")} 1</div>
              <h2 className="font-display font-bold text-xl text-slate-100 mb-1">{t("importTs.step1Title")}</h2>
              <p className="text-slate-400 text-sm mb-6">{t("importTs.step1Body")}</p>

              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-slate-700 rounded-lg p-10 text-center cursor-pointer hover:border-blue-500/60 hover:bg-blue-500/5 transition-colors"
                data-testid="ts-upload-zone"
              >
                {file ? (
                  <div className="space-y-2">
                    <div className="w-12 h-12 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center">
                      <FileSearch size={20} className="text-blue-400"/>
                    </div>
                    <div className="font-display text-slate-100 font-medium">{file.name}</div>
                    <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)} KB · {t("importTs.fileChange")}</div>
                  </div>
                ) : (
                  <>
                    <div className="w-14 h-14 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center mb-3">
                      <Upload size={22} className="text-blue-400"/>
                    </div>
                    <div className="font-display font-medium text-slate-100">{t("importTs.dropOrClick")}</div>
                    <div className="text-slate-500 text-sm mt-1">{t("importTs.dropSubtitle")}</div>
                  </>
                )}
              </div>
              <input ref={fileRef} type="file" accept=".pdf,.xlsx,.csv,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain" onChange={(e) => handleFile(e.target.files[0])} className="hidden" data-testid="ts-file-input"/>

              {file && (
                <button onClick={parsePDF} disabled={parsing} className="btn-primary mt-6 px-6 py-3 rounded-md flex items-center gap-2" data-testid="ts-parse-btn">
                  <Sparkles size={16}/> {parsing ? t("importTs.analyzing") : t("importTs.analyzeAI")}
                </button>
              )}
            </div>
          )}

          {step === 2 && a && (
            <div data-testid="step-verify">
              <div className="label-tech mb-2">{t("common.step")} 2</div>
              <h2 className="font-display font-bold text-xl text-slate-100 mb-1">{t("importTs.step2Title")}</h2>
              <p className="text-slate-400 text-sm mb-6">{t("importTs.step2Body", { n: a.operations?.length || 0 })}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <InfoBlock label={t("importTs.infoPart")} value={a.part_description}/>
                <InfoBlock label={t("importTs.infoMaterialDetected")} value={a.material_hint || "—"}/>
                <InfoBlock label={t("importTs.infoMachineDetected")} value={a.machine_hint || "—"}/>
                <InfoBlock label={t("importTs.infoComplexity")} value={a.complexity}/>
                <InfoBlock label={t("importTs.infoCycleTime")} value={a.total_cycle_minutes_from_pdf ? `${a.total_cycle_minutes_from_pdf} min` : "—"}/>
                <InfoBlock label={t("importTs.infoOpsMapped")} value={`${a.operations?.length || 0}`}/>
              </div>

              <div className="tech-card overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-700/60 label-tech">{t("importTs.operations")}</div>
                <div className="max-h-80 overflow-auto divide-y divide-slate-800">
                  {(a.operations || []).map((op, i) => (
                    <div key={`ts-op-${i}`} className="px-5 py-2.5 flex items-center gap-3" data-testid={`ts-op-${i}`}>
                      <div className="font-mono-data text-xs text-slate-500 w-6">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-100 truncate">{op.notes || op.type}</div>
                        <div className="text-[11px] text-slate-500">{op.type}</div>
                      </div>
                      <div className="font-mono-data text-sm text-blue-400 flex items-center gap-1">
                        <Clock size={11}/> {Number(op.estimated_minutes || 0).toFixed(2)}'
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-config">
              <div className="label-tech mb-2">{t("common.step")} 3</div>
              <h2 className="font-display font-bold text-xl text-slate-100 mb-1">{t("importTs.step3Title")}</h2>

              <label className="label-tech block mt-4 mb-2">{t("newQuote.stepLabel2")}</label>
              <select value={materialId} onChange={(e) => setMaterialId(e.target.value)} className="w-full px-4 py-3 rounded-md text-sm" data-testid="ts-material-select">
                <option value="">{t("importTs.materialSelect")}</option>
                {materials.map((m) => <option key={m.id} value={m.id}>{m.name} · €{m.price_per_kg}/kg</option>)}
              </select>

              <label className="label-tech block mt-6 mb-2">{t("importTs.machinesInWorkshop")}</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-auto p-2 border border-slate-700 rounded-md bg-slate-900/40">
                {machines.map((m) => {
                  const checked = ownedMachines.includes(m.id);
                  return (
                    <label key={m.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm ${checked ? "bg-blue-500/10 text-slate-100" : "text-slate-300 hover:bg-slate-800"}`} data-testid={`ts-machine-${m.id}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleMachine(m.id)} className="accent-blue-500"/>
                      <span className="flex-1 truncate">{m.name}</span>
                      <span className="text-xs text-slate-500 font-mono-data">€{m.hourly_rate}/h</span>
                    </label>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div>
                  <label className="label-tech block mb-2">{t("importTs.batch")}</label>
                  <input type="number" min="1" value={batchSize} onChange={(e) => setBatchSize(e.target.value)} className="w-full px-3 py-2 rounded-md text-sm" data-testid="ts-batch"/>
                </div>
                <div>
                  <label className="label-tech block mb-2">{t("importTs.margin")}</label>
                  <input type="number" min="0" max="200" value={marginPct} onChange={(e) => setMarginPct(e.target.value)} className="w-full px-3 py-2 rounded-md text-sm" data-testid="ts-margin"/>
                </div>
                <div>
                  <label className="label-tech block mb-2">{t("importTs.currency")}</label>
                  <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="w-full px-3 py-2 rounded-md text-sm" data-testid="ts-currency">
                    {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-tech block mb-2">{t("importTs.customer")}</label>
                  <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="w-full px-3 py-2 rounded-md text-sm" data-testid="ts-customer">
                    <option value="">{t("importTs.noCustomer")}</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="label-tech block mb-2">{t("importTs.notes")}</label>
                <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-4 py-3 rounded-md text-sm" data-testid="ts-notes"/>
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-save" className="text-center py-8">
              {saving ? (
                <>
                  <div className="w-16 h-16 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center mb-4 pulse-blue"><Sparkles size={28} className="text-blue-400 animate-pulse"/></div>
                  <h2 className="font-display font-bold text-xl text-slate-100 mb-2">{t("importTs.calculating")}</h2>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center mb-4"><Cog size={28} className="text-blue-400"/></div>
                  <h2 className="font-display font-bold text-xl text-slate-100 mb-2">{t("importTs.readyTitle")}</h2>
                  <p className="text-slate-400 text-sm mb-6">{t("newQuote.machinesCount", { n: ownedMachines.length })} · {t("importTs.batch").toLowerCase()} {batchSize} · {currency} · {t("importTs.margin").toLowerCase()} {marginPct}%{customerId ? ` · ${t("importTs.customer").toLowerCase()} ${customers.find(c=>c.id===customerId)?.name}` : ""}</p>
                  <button onClick={submit} className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-md" data-testid="ts-save-btn">
                    <Sparkles size={16}/> {t("importTs.createQuote")}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between mt-6">
          <button onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1 || parsing || saving} className="px-4 py-2 rounded-md text-sm text-slate-300 border border-slate-700 hover:border-slate-500 disabled:opacity-40 flex items-center gap-2">
            <ArrowLeft size={14}/> {t("common.back")}
          </button>
          {step < 4 && step !== 1 && (
            <button onClick={() => setStep((s) => Math.min(4, s + 1))} disabled={!canNext()} className="btn-primary px-5 py-2.5 rounded-md flex items-center gap-2" data-testid="ts-next-step">
              {t("common.next")} <ArrowRight size={14}/>
            </button>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

function InfoBlock({ label, value }) {
  return (
    <div className="bg-slate-900/40 border border-slate-700 rounded-md p-3">
      <div className="label-tech mb-1">{label}</div>
      <div className="text-sm text-slate-100 font-display">{value}</div>
    </div>
  );
}
